import React, { Fragment, useEffect, useState } from "react";
import { SecondaryAppBar } from "../../common/SecondaryAppBar";
import { usersCachingService, teamsCachingService } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import { loadRoles, loadUserRoles } from "../../../redux/actions";
import { selectRoles, selectUserRoles, selectCaseRoles } from "../../../redux/selectors";
import { RoleDialog } from "./RoleDialog";
import { RoleRevertDialog } from "./RoleRevertDialog";
import { Add, Lock, LockOpen, Block } from "@material-ui/icons";
import { Grid, Button, Typography, CircularProgress, Avatar } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useAuthorizationForCase } from "../../../hooks/useAuthorization";
import { CaseRoles, getRoleName, getRoleNames } from "../../../auth/CaseRoles";
import { useFieldLookup } from "../../../hooks/useFieldLookup";
import { GrantAccessDialog } from "./GrantAccessDialog";
import { RevokeRoleBasedAccessDialog } from "./RevokeRoleBasedAccessDialog";
import { Link } from "react-router-dom";
const sortRoles = (roles) => {
    // atm best UX if the roles stay in the same order as the user interacts with page
    return roles;
};
export const Roles = ({ model }) => {
    const dispatch = useDispatch();
    const [openDialog, setOpenDialog] = useState(false);
    const [roleToModify, setRoleToModify] = useState();
    const openModifyDialog = (roleToRevert, dialogToOpen) => {
        setRoleToModify(roleToRevert);
        setOpenDialog(dialogToOpen);
    };
    useEffect(() => {
        dispatch(loadRoles(model.id));
        dispatch(loadUserRoles());
    }, [model, dispatch]);
    const isAuthedForAddRole = useAuthorizationForCase(model.id, CaseRoles.admin, CaseRoles.globalAdmin);
    const { loadingState } = useSelector(selectRoles);
    const data = useSelector(selectCaseRoles);
    const userRoles = useSelector(selectUserRoles);
    const caseRoles = sortRoles(data || []);
    return (React.createElement(Fragment, null,
        React.createElement(SecondaryAppBar, { hasSubBar: true, title: React.createElement(React.Fragment, null,
                React.createElement(Link, { to: `/complaints/list`, "data-test-id": "secondary-header-root-link" }, "/ All cases"),
                " ",
                "/ ",
                model.title), actions: loadingState === "LOADED" // only render the buttons when data has loaded to keep e2e tests from losing the reference to the button
                ? [
                    {
                        title: "Assign Role",
                        onClick: () => setOpenDialog("assign"),
                        startIcon: React.createElement(Add, null),
                        disabled: !isAuthedForAddRole,
                        "data-test": "case-role",
                    },
                ]
                : [] }, loadingState === "LOADING" && React.createElement(CircularProgress, { size: 24, color: "secondary" })),
        React.createElement(Typography, { variant: "h6" }, "General Permissions"),
        React.createElement(Grid, { container: true, spacing: 6 }, userRoles.map(item => (React.createElement(UserRole, { key: item.upn, item: item, allowGrantOrRevoke: isAuthedForAddRole, grantAccess: () => openModifyDialog(item, "grant"), revokeAccess: () => openModifyDialog(item, "revoke") })))),
        React.createElement("br", null),
        React.createElement("br", null),
        React.createElement(Typography, { variant: "h6" }, "Case Level Permissions"),
        React.createElement(Grid, { container: true, spacing: 6 },
            loadingState === "LOADED" &&
                !!caseRoles.length &&
                caseRoles.map(item => (React.createElement(Role, { allowRevoke: isAuthedForAddRole, key: item.caseRoleId, item: item, onClick: () => openModifyDialog(item, "revert") }))),
            loadingState === "LOADED" && !caseRoles.length && (React.createElement(Grid, { item: true },
                React.createElement(Typography, { variant: "body1" }, "This case has no case level permissions")))),
        React.createElement(Fragment, null,
            openDialog === "assign" && (React.createElement(RoleDialog, { caseId: model.id, isDialogOpen: openDialog === "assign", onDialogClose: () => setOpenDialog(false) })),
            openDialog === "revert" && roleToModify && (React.createElement(RoleRevertDialog, { model: roleToModify, isDialogOpen: openDialog === "revert", onDialogClose: () => setOpenDialog(false) })),
            openDialog === "grant" && roleToModify && (React.createElement(GrantAccessDialog, { caseId: model.id, model: roleToModify, isOpen: openDialog === "grant", onDialogClose: () => setOpenDialog(false) })),
            openDialog === "revoke" && roleToModify && (React.createElement(RevokeRoleBasedAccessDialog, { caseId: model.id, model: roleToModify, isOpen: openDialog === "revoke", onDialogClose: () => setOpenDialog(false) })))));
};
const Role = ({ item, allowRevoke, onClick, }) => {
    const userName = useFieldLookup(usersCachingService, item.userId) || item.userId;
    const teamName = useFieldLookup(teamsCachingService, item.teamId) || "None";
    const roleName = getRoleName(item.roleType);
    const isReadyToShow = userName && teamName && roleName;
    const calculateInitials = (name) => {
        const splitName = name.split(" ");
        let initials = "";
        splitName.forEach((section) => (initials = initials.concat(section[0])));
        return initials;
    };
    return (React.createElement(Grid, { item: true, xs: 3, key: item.caseRoleId },
        React.createElement("div", { className: "RoleContainer" }, isReadyToShow ? (React.createElement("div", null,
            React.createElement("div", null,
                React.createElement(Avatar, { style: { backgroundColor: "#293445" } }, calculateInitials(userName))),
            React.createElement(Typography, { "data-test": "role-username" },
                React.createElement("strong", null, userName)),
            React.createElement("br", null),
            React.createElement(Typography, { "data-test": "role-team" },
                React.createElement("strong", null, "Team:"),
                " ",
                teamName),
            React.createElement(Typography, { "data-test": "role-role" },
                React.createElement("strong", null, "Role:"),
                " ",
                roleName),
            React.createElement("br", null),
            React.createElement("br", null),
            allowRevoke && (React.createElement("div", { className: "RoleActions" }, item.isDenial ? (React.createElement(Button, { variant: "outlined", size: "small", color: "secondary", onClick: () => onClick(), disabled: true },
                React.createElement(Block, { scale: "sm" }),
                " Revoked")) : (React.createElement(Button, { variant: "outlined", size: "small", color: "secondary", onClick: () => onClick(), "data-test": "case-role-revoke" },
                React.createElement(Lock, { scale: "sm" }),
                " Revoke Access")))))) : (React.createElement("div", null,
            React.createElement(Skeleton, { variant: "text", height: 10 }),
            React.createElement(Skeleton, { variant: "text", height: 10 }),
            React.createElement(Skeleton, { variant: "text", height: 10 }))))));
};
const UserRole = ({ item, allowGrantOrRevoke, grantAccess, revokeAccess }) => {
    //const userName = useFieldLookup(usersCachingService, item.upn);
    const roleNames = getRoleNames(item.roles);
    const calculateInitials = (name) => {
        const splitName = name.split(" ");
        let initials = "";
        splitName.forEach((section) => (initials = initials.concat(section[0])));
        return initials;
    };
    const isReadyToShow = !!roleNames && roleNames.length > 0;
    return (React.createElement(Grid, { item: true, xs: 3, key: item.upn },
        React.createElement("div", { className: `RoleContainer ${item.isDenied ? "DeniedUserRoleContainer" : "UserRoleContainer"}` }, isReadyToShow ? (React.createElement("div", null,
            React.createElement("div", null,
                React.createElement(Avatar, { style: { backgroundColor: "#293445" } }, calculateInitials(item.fullName))),
            React.createElement(Typography, { "data-test": "user-role-fullname" },
                React.createElement("strong", null, item.fullName)),
            React.createElement(Typography, { "data-test": "user-role-roles" },
                React.createElement("strong", null, "Roles:"),
                " ",
                roleNames.join(", ")),
            React.createElement(Typography, { "data-test": "user-role-access" },
                React.createElement("strong", null, "Access:"),
                " ",
                item.isDenied ? "Denied" : "Allowed"),
            React.createElement("br", null),
            React.createElement("br", null),
            allowGrantOrRevoke && (React.createElement("div", { className: "RoleActions" }, item.isDenied ? (React.createElement(Button, { size: "small", variant: "outlined", color: "secondary", onClick: () => grantAccess(), "data-test": "case-user-role-re-grant" },
                React.createElement(LockOpen, { scale: "sm" }),
                " Grant access")) : (React.createElement(Button, { size: "small", variant: "outlined", color: "secondary", onClick: () => revokeAccess(), "data-test": "case-user-role-revoke" },
                React.createElement(Lock, { scale: "sm" }),
                " Revoke Access")))))) : (React.createElement("div", null,
            React.createElement(Skeleton, { variant: "text", height: 10 }),
            React.createElement(Skeleton, { variant: "text", height: 10 }),
            React.createElement(Skeleton, { variant: "text", height: 10 }))))));
};
